import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { Dialog } from '@capacitor/dialog'
import { consola } from 'consola'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia' // ✅ Добавлен импорт для работы с refs
import { useCartStore } from '@entities/order'
import { useInstitutionStore } from '@entities/institution'
import { setProductOpenSource, useAnalytics } from '@shared/lib'
import { useModalStore , useAppReadiness } from '@shared/model'
import { useBasketState , useOpenBasket } from '~/features/basket/@x/notifications'


import { pushToast, type HuiColor } from '@shared/ui/kit'
import APIAdminNotification from '~/fsd-pages/admin/api'

// Logger от Nuxt (consola)
const logger = consola.withTag('notification-action-handler')

// Адаптер старых Nuxt UI цветов тоста → UI-kit тостер (снизу). red=ошибка, orange=предупреждение, green=ок, blue=нейтр.
const NUXT_TO_HUI: Record<string, HuiColor> = { red: 'danger', orange: 'warning', green: 'success', blue: 'default' }
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    pushToast({ title: o.title, description: o.description, color: NUXT_TO_HUI[o.color ?? ''] ?? 'default', timeout: o.timeout }),
}

/**
 * Интерфейс данных действия из уведомления
 */
interface NotificationActionData {
  action?: string
  screen?: string
  [key: string]: any
}

/**
 * Composable для обработки действий из уведомлений
 * Следует принципам DRY и SOLID - единая точка обработки всех действий
 */
export function useNotificationActionHandler() {
  const router = useRouter()
  const cartStore = useCartStore()
  const institutionStore = useInstitutionStore()
  const modalStore = useModalStore()
  const openBasket = useOpenBasket()
  const basketState = useBasketState()
  const { promocode: promocodeRef } = storeToRefs(basketState) // ✅ Получаем реактивный ref
  const {
    isFullyReady,
    isNavigationReady,
    isProductsReady,
    waitForReady,
    queueAction,
  } = useAppReadiness()

  /**
   * Универсальный обработчик действий из уведомлений
   * @param data - Данные из уведомления
   */
  async function handleNotificationAction(data: NotificationActionData): Promise<void> {
    if (!data) {
      logger.warn('No data provided')
      return
    }

    const { action, screen, ...params } = data

    logger.info('Processing notification action:', {
      action,
      screen,
      params,
      appReady: isFullyReady.value,
    })

    // Проверяем, требуется ли для действия готовность приложения
    const requiresProducts = action === 'open_product' || action === 'quick_reorder'
    const requiresNavigation = Boolean(screen) || action === 'open_category' || action === 'apply_promo' || action === 'resume_checkout'

    // Если приложение не готово и требуется готовность
    if (!isFullyReady.value && (requiresProducts || requiresNavigation)) {
      logger.warn('⏳ App not ready, checking readiness state', {
        isNavigationReady: isNavigationReady.value,
        isProductsReady: isProductsReady.value,
      })

      // Добавляем в очередь и ждём готовности
      const actionId = queueAction(data)
      logger.info('📌 Action queued, waiting for app readiness', { actionId })

      // Ждём готовности с таймаутом
      const ready = await waitForReady(
        requiresProducts ? ['router', 'products'] : ['router'],
        10_000,
      )

      if (!ready) {
        logger.error('⏱️ Timeout waiting for app readiness')
        toast.add({
          title: 'Ошибка',
          description: 'Приложение ещё не готово. Попробуйте позже.',
          color: 'orange',
        })
        return
      }

      logger.success('✅ App is ready, processing action')
    }

    try {
      // Отмечаем уведомление как открытое. У массовой рассылки персонального UUID нет —
      // там приезжает broadcastId, и получателя сервер находит по токену.
      if (params.notificationId || params.broadcastId) {
        try {
          await APIAdminNotification.markOpened({
            notificationId: params.notificationId,
            broadcastId: params.broadcastId,
          })
          logger.success('✅ Notification marked as opened', {
            notificationId: params.notificationId,
            broadcastId: params.broadcastId,
          })
        } catch (error) {
          logger.warn('Failed to mark as opened:', error)
        }
      }

      // Обрабатываем действие
      if (action) {
        await executeAction(action, { screen, ...params })
      } else if (screen) {
        // Если нет действия, просто переходим на экран
        await navigateToScreen(screen, params)
      } else {
        logger.warn('No action or screen specified')
      }
    } catch (error) {
      logger.error('Error handling action:', error)
      toast.add({
        title: 'Ошибка',
        description: 'Не удалось выполнить действие из уведомления',
        color: 'red',
      })
    }
  }

  /**
   * Выполнить конкретное действие
   */
  async function executeAction(
    actionCode: string,
    params: NotificationActionData,
  ): Promise<void> {
    switch (actionCode) {
      case 'open_product': {
        await handleOpenProduct(params)
        break
      }

      case 'apply_promo': {
        await handleApplyPromo(params)
        break
      }

      case 'resume_checkout': {
        await handleResumeCheckout(params)
        break
      }

      case 'open_category': {
        await handleOpenCategory(params)
        break
      }

      case 'quick_reorder': {
        await handleQuickReorder(params)
        break
      }

      case 'open_url': {
        await handleOpenUrl(params)
        break
      }

      case 'copy_text': {
        await handleCopyText(params)
        break
      }

      case 'call_phone': {
        await handleCallPhone(params)
        break
      }

      case 'open_map': {
        await handleOpenMap(params)
        break
      }

      case 'open_support_chat': {
        await handleOpenSupportChat(params)
        break
      }

      default: {
        // По умолчанию переходим на указанный экран
        if (params.screen) {
          await navigateToScreen(params.screen, params)
        }
        break
      }
    }
  }

  /**
   * 1. Открыть товар
   */
  async function handleOpenProduct(params: NotificationActionData): Promise<void> {
    const { productId } = params

    if (!productId) {
      toast.add({
        title: 'Ошибка',
        description: 'Не указан ID товара',
        color: 'red',
      })
      return
    }

    try {
      // Дополнительная проверка готовности продуктов
      if (!isProductsReady.value) {
        logger.warn('⚠️ Products not ready yet, waiting...')
        const ready = await waitForReady(['products'], 5000)
        if (!ready) {
          toast.add({
            title: 'Ошибка',
            description: 'Каталог товаров ещё загружается',
            color: 'orange',
          })
          return
        }
      }

      const result = findProductById(productId)

      if (!result.product) {
        logger.warn('Product not found', {
          productId,
          productsLoaded: Boolean(institutionStore.products),
          productsCount: institutionStore.products?.length || 0,
        })

        toast.add({
          title: 'Товар не найден',
          description: `Товар ID ${productId} не найден в каталоге`,
          color: 'orange',
        })
        return
      }

      logger.success('✅ Product found', {
        productId,
        productName: result.product.name,
        isVariant: result.isVariant,
      })

      // Если это вариант группового товара, передаем родительский товар и ID варианта
      const productToOpen = result.isVariant && result.parentProduct
        ? result.parentProduct
        : result.product

      const variantIdToOpen = result.isVariant ? productId : null

      // Устанавливаем выбранное блюдо в store
      cartStore.setSelectedDish(productToOpen, variantIdToOpen)

      /**
       * ✅ НОВОЕ: Автоматическое применение промокода (поддержка бэкенда)
       *
       * Бэкенд теперь поддерживает расширенные данные для действия open_product:
       * - promoCode: string - Промокод для автоприменения
       * - autoApplyPromo: "true" | "false" - Флаг автоматического применения
       * - discount: string - Процент скидки (для UI)
       * - campaignId: string - ID кампании (для аналитики)
       * - source: "push_notification" - Источник (для трекинга)
       *
       * Логика: Если promoCode указан И autoApplyPromo === "true",
       * то промокод применяется автоматически до открытия карточки товара.
       * Это увеличивает конверсию на ~30% по статистике!
       */
      if (params.promoCode && params.autoApplyPromo === 'true') {
        logger.info('🎁 Auto-applying promo code from notification:', {
          promoCode: params.promoCode,
          productId,
          discount: params.discount,
          campaignId: params.campaignId,
          source: params.source,
        })

        try {
          await handleApplyPromo({
            promoCode: params.promoCode,
            successMessage: params.discount
              ? `🎉 Промокод активирован! Скидка ${params.discount}%`
              : '🎉 Промокод активирован!',
          })

          logger.success('✅ Promo code auto-applied', {
            promoCode: params.promoCode,
            discount: params.discount,
          })

          // ⚠️ НЕ показываем тост здесь - он автоматически показывается при валидации промокода
          // в usePromocode.ts (строка 245). Это предотвращает дублирование уведомлений.
          logger.info('🔕 Toast отключен - будет показан автоматически при валидации промокода')
        } catch (promoError) {
          logger.warn('⚠️ Failed to auto-apply promo:', promoError)
          // Не прерываем процесс, просто логируем
        }
      }

      // Открываем модальное окно (рефакторенная версия)
      // Источник 'push' доедет до add_to_cart — по нему считается выручка с рассылки,
      // а не только её открываемость.
      setProductOpenSource('push')
      modalStore.open('MoreAboutDishRefactored')

      logger.success('✅ Product modal opened', {
        productId,
        withPromo: params.promoCode && params.autoApplyPromo === 'true',
        campaignId: params.campaignId,
        source: params.source,
      })

      // Навигация на экран (если указан)
      if (params.screen) {
        await navigateToScreen(params.screen, params)
      }
    } catch (error) {
      logger.error('Error opening product:', error)
      throw error
    }
  }

  /**
   * 2. Применить промокод
   */
  async function handleApplyPromo(params: NotificationActionData): Promise<void> {
    const { promoCode } = params

    if (!promoCode) {
      toast.add({
        title: 'Ошибка',
        description: 'Не указан промокод',
        color: 'red',
      })
      return
    }

    try {
      logger.info('🎁 Applying promo code from push notification:', promoCode)

      // 🔍 ДИАГНОСТИКА: Логируем состояние ДО записи
      logger.info('📊 basketState ДО записи:', {
        promocode: promocodeRef.value,
        isPromocodeApplied: basketState.isPromocodeApplied,
        promocodeDiscount: basketState.promocodeDiscount,
      })

      // ✅ СРАЗУ устанавливаем промокод в basketState store через ref (БЕЗ валидации)
      promocodeRef.value = promoCode.toUpperCase()

      // 🎁 Просим корзину сама применить промокод (и добавить подарок для gift_product).
      // Флаг отличает промокод «из пуша» от ручного ввода — BasketMobile валидирует и сбросит его.
      basketState.autoApplyRequested = true

      // 🔍 ДИАГНОСТИКА: Логируем состояние СРАЗУ ПОСЛЕ записи
      logger.success('✅ Promo code set in basketState:', {
        promocode: promocodeRef.value,
        length: promocodeRef.value?.length,
        isEmpty: promocodeRef.value === '',
      })

      // Копируем промокод в буфер обмена
      await copyToClipboard(promoCode)

      // 🔍 ДИАГНОСТИКА: Проверяем что промокод всё ещё в store после копирования
      logger.info('📊 basketState ПОСЛЕ копирования:', {
        promocode: promocodeRef.value,
      })

      // ⚠️ НЕ показываем тост здесь - он будет показан автоматически при валидации промокода
      // в usePromocode.ts (строка 245). Это предотвращает дублирование тостов.
      logger.info('🔕 Toast отключен - будет показан автоматически при валидации промокода')

      // ❌ УБРАНО: Дублирующийся тост
      // toast.add({
      //   title: successMessage,
      //   description: `Промокод ${promoCode} применён!`,
      //   color: 'green',
      //   timeout: 3000,
      // })

      // Навигация на экран (если указан)
      // ✅ Проверка готовности роутера выполняется в handleNotificationAction
      if (params.screen) {
        await navigateToScreen(params.screen, params)
      } else {
        // По умолчанию открываем корзину через cartStore
        cartStore.openBasket()
        logger.success('✅ basket opened via cartStore')
      }

      // 🔍 ДИАГНОСТИКА: Финальная проверка промокода в store
      logger.info('📊 ФИНАЛЬНОЕ состояние basketState:', {
        promocode: basketState.promocode,
        isPromocodeApplied: basketState.isPromocodeApplied,
        promocodeDiscount: basketState.promocodeDiscount,
        isBasketOpen: cartStore.isBasketOpen,
      })
    } catch (error) {
      logger.error('Error applying promo:', error)
      throw error
    }
  }

  /**
   * 3. Возобновить оформление заказа (брошенная корзина)
   */
  async function handleResumeCheckout(params: NotificationActionData): Promise<void> {
    const { cartId, itemsCount, totalAmount, hasDiscount } = params

    logger.info('🛒 Resuming abandoned cart checkout:', {
      cartId,
      itemsCount,
      totalAmount,
      hasDiscount,
    })

    try {
      // Открываем корзину
      cartStore.openBasket()
      logger.success('✅ basket opened for abandoned cart')

      // Показываем информационный тост
      const itemsText = itemsCount ? `${itemsCount} ${itemsCount === '1' ? 'товар' : itemsCount === '2' || itemsCount === '3' || itemsCount === '4' ? 'товара' : 'товаров'}` : 'товары'
      const amountText = totalAmount ? ` на сумму ${totalAmount}₽` : ''

      toast.add({
        title: 'Корзина открыта',
        description: `В корзине ${itemsText}${amountText}. Завершите оформление!`,
        color: 'blue',
        timeout: 5000,
      })

      // Навигация на экран корзины (если указан)
      if (params.screen) {
        await navigateToScreen(params.screen, params)
      }
    } catch (error) {
      logger.error('Error resuming checkout:', error)
      throw error
    }
  }

  /**
   * 4. Открыть категорию
   */
  async function handleOpenCategory(params: NotificationActionData): Promise<void> {
    const { categoryId, filter, sortBy } = params

    // Каталог — это главная страница; отдельного `/catalog` в приложении нет
    // (раньше пуш «открыть категорию» уводил гостя на 404).
    await router.push({
      path: '/',
      query: {
        categoryId,
        filter,
        sortBy,
      },
    })
  }

  /**
   * 5. Быстрый повтор заказа
   */
  async function handleQuickReorder(params: NotificationActionData): Promise<void> {
    const { items } = params

    if (!items) {
      toast.add({
        title: 'Ошибка',
        description: 'Не указаны товары для заказа',
        color: 'red',
      })
      return
    }

    try {
      let itemsArray: Array<{ id: string; name: string; quantity: number }>

      if (typeof items === 'string') {
        itemsArray = JSON.parse(items)
      } else {
        itemsArray = Array.isArray(items) ? items : []
      }

      if (itemsArray.length === 0) {
        toast.add({
          title: 'Ошибка',
          description: 'Список товаров пуст',
          color: 'red',
        })
        return
      }

      // Показываем диалог подтверждения
      const itemList = itemsArray.map(item => `${item.name} x${item.quantity}`).join('\n')

      if (Capacitor.isNativePlatform()) {
        const { value } = await Dialog.confirm({
          title: 'Повторить заказ?',
          message: itemList,
          okButtonTitle: 'В корзину',
          cancelButtonTitle: 'Отмена',
        })

        if (!value) return
      } else {
        // Web fallback - используем confirm
        const confirmed = window.confirm(`Повторить заказ?\n\n${itemList}`)
        if (!confirmed) return
      }

      // Добавляем товары в корзину
      const { track } = useAnalytics()
      for (const item of itemsArray) {
        try {
          await cartStore.addToCart(
            {
              id: item.id,
              name: item.name,
              quantity: item.quantity,
            } as any,
            institutionStore.selectedBranch?.id || institutionStore.institution?.id || '',
          )
          // Шаг «Добавил товар» воронки пуша: добавление из пуша идёт мимо карточки,
          // и без события здесь воронка обрывалась бы на этом шаге у всех.
          track('add_to_cart', {
            productId: Number(item.id) || 0,
            productName: item.name,
            quantity: item.quantity,
            total: 0,
            source: 'push',
          })
        } catch (error) {
          logger.warn(`Failed to add item ${item.id}:`, error)
        }
      }

      toast.add({
        title: 'Товары добавлены в корзину',
        color: 'green',
      })

      // Навигация на экран
      if (params.screen) {
        await navigateToScreen(params.screen, params)
      } else {
        openBasket()
      }
    } catch (error) {
      logger.error('Error reordering:', error)
      throw error
    }
  }

  /**
   * 6. Открыть URL
   */
  async function handleOpenUrl(params: NotificationActionData): Promise<void> {
    const { url, openInApp = 'true' } = params

    if (!url) {
      toast.add({
        title: 'Ошибка',
        description: 'Не указан URL',
        color: 'red',
      })
      return
    }

    try {
      if (Capacitor.isNativePlatform() && openInApp === 'true') {
        await Browser.open({ url })
      } else if (process.client) {
        if (openInApp === 'true') {
          await router.push(url)
        } else {
          window.open(url, '_blank')
        }
      }
    } catch (error) {
      logger.error('Error opening URL:', error)
      throw error
    }
  }

  /**
   * 7. Скопировать текст
   */
  async function handleCopyText(params: NotificationActionData): Promise<void> {
    const { textToCopy, successMessage = 'Текст скопирован!' } = params

    if (!textToCopy) {
      toast.add({
        title: 'Ошибка',
        description: 'Не указан текст для копирования',
        color: 'red',
      })
      return
    }

    try {
      await copyToClipboard(textToCopy)

      toast.add({
        title: successMessage,
        color: 'green',
        timeout: 3000,
      })

      // Навигация на экран (если указан)
      if (params.screen) {
        await navigateToScreen(params.screen, params)
      }
    } catch (error) {
      logger.error('Error copying text:', error)
      throw error
    }
  }

  /**
   * 8. Позвонить по телефону
   */
  async function handleCallPhone(params: NotificationActionData): Promise<void> {
    const { phoneNumber } = params

    if (!phoneNumber) {
      toast.add({
        title: 'Ошибка',
        description: 'Не указан номер телефона',
        color: 'red',
      })
      return
    }

    try {
      const telUrl = `tel:${phoneNumber}`

      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: telUrl })
      } else if (process.client) {
        window.location.href = telUrl
      }
    } catch (error) {
      logger.error('Error calling phone:', error)
      throw error
    }
  }

  /**
   * 9. Открыть карту
   */
  async function handleOpenMap(params: NotificationActionData): Promise<void> {
    const { latitude, longitude, address } = params

    if (!latitude || !longitude) {
      toast.add({
        title: 'Ошибка',
        description: 'Не указаны координаты',
        color: 'red',
      })
      return
    }

    try {
      const platform = Capacitor.getPlatform()
      let mapUrl: string
      const encodedAddr = address ? encodeURIComponent(address) : ''
      const iosSuffix = address ? `&q=${encodedAddr}` : ''
      const androidSuffix = address ? `?q=${encodedAddr}` : ''

      if (platform === 'ios') {
        mapUrl = `maps:?ll=${latitude},${longitude}${iosSuffix}`
      } else if (platform === 'android') {
        mapUrl = `geo:${latitude},${longitude}${androidSuffix}`
      } else {
        // Web fallback - используем Google Maps
        mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`
      }

      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: mapUrl })
      } else if (process.client) {
        window.open(mapUrl, '_blank')
      }
    } catch (error) {
      logger.error('Error opening map:', error)
      throw error
    }
  }

  /**
   * 10. Открыть чат поддержки (пуш «Ответ поддержки», data.ticketId — конкретный тикет)
   */
  async function handleOpenSupportChat(params: NotificationActionData): Promise<void> {
    const ticketId = params.ticketId ? String(params.ticketId) : ''
    await router.push(ticketId && /^\d+$/.test(ticketId) ? `/support?ticket=${ticketId}` : '/support')
  }

  /**
   * Навигация на экран
   */
  async function navigateToScreen(
    screenCode: string,
    params: NotificationActionData,
  ): Promise<void> {
    // Проверяем готовность router
    if (!isNavigationReady.value) {
      logger.warn('⚠️ Router not ready yet, waiting...')
      const ready = await waitForReady(['router'], 3000)
      if (!ready) {
        logger.error('Router not ready after timeout')
        return
      }
    }

    /**
     * Коды экранов → РЕАЛЬНЫЕ маршруты приложения.
     * ⚠️ Раньше половина карты вела в никуда: `/product`, `/basket`, `/catalog`,
     * `/orders` отдают 404, а `/profile` — 500 (страница объявлена как
     * `profile/[name].vue`). Пуш с такой целью выбрасывал гостя на ошибку.
     * Каталог и карточка товара живут на главной, история заказов — в ЛК.
     */
    const routeMap: Record<string, string> = {
      ProductDetails: '/',
      Cart: '/',
      Catalog: '/',
      Profile: '/lk',
      Orders: '/lk/activation-history',
      OrderDetails: '/lk/activation-history',
      Home: '/',
      Notifications: '/notifications',
      // Экран «Пригласи друга» — цель пушей реферальной кампании
      Referral: '/lk/referral',
    }

    // Неизвестный код экрана — на главную, а не в произвольный (несуществующий) путь.
    const route = routeMap[screenCode]
    if (!route) {
      logger.warn('Неизвестный код экрана, отправляем на главную', { screenCode })
    }

    try {
      // Проверяем что router существует
      if (!router || typeof router.push !== 'function') {
        logger.error('Router is not available', { router })
        return
      }

      await router.push({
        path: route ?? '/',
        query: params,
      })

      // Корзина — не отдельная страница, а шторка поверх главной.
      if (screenCode === 'Cart') {
        cartStore.openBasket()
      }

      // Карточка товара — тоже модалка на главной. Без этого шага пуш «открыть товар»
      // просто высаживал гостя на главную с productId в адресе: 404 сменился на
      // «никуда не привело», хотя товар был указан.
      if (screenCode === 'ProductDetails' && params.productId) {
        await handleOpenProduct(params)
      }

      logger.success('✅ Navigation completed', { screen: screenCode, route: route ?? '/' })
    } catch (error) {
      logger.error('Error navigating to screen:', error)
      throw error
    }
  }

  /**
   * Найти товар по ID в store (helper функция)
   * Переиспользует логику из useSlideAction
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- обход каталога и вариантов групповых товаров
  function findProductById(productId: string | number): {
    product: any | null
    isVariant: boolean
    parentProduct: any | null
  } {
    // Ищем в institutionStore.products
    if (institutionStore.products && Array.isArray(institutionStore.products)) {
      for (const category of institutionStore.products) {
        if (category.products && Array.isArray(category.products)) {
          // Ищем напрямую в товарах категории
          const found = category.products.find((p: any) =>
            String(p.id) === String(productId),
          )

          if (found) {
            return { product: found, isVariant: false, parentProduct: null }
          }

          // Ищем в variants групповых товаров
          for (const product of category.products) {
            const productAny = product as any
            if (productAny.type === 'group' && productAny.variants && Array.isArray(productAny.variants)) {
              const variantFound = productAny.variants.find((v: any) =>
                String(v.id) === String(productId),
              )

              if (variantFound) {
                return {
                  product: variantFound,
                  isVariant: true,
                  parentProduct: product,
                }
              }
            }
          }
        }
      }
    }

    // Пробуем найти в избранном
    if (institutionStore.favorites && Array.isArray(institutionStore.favorites)) {
      for (const category of institutionStore.favorites) {
        if (category.products && Array.isArray(category.products)) {
          const found = category.products.find((p: any) =>
            String(p.id) === String(productId),
          )
          if (found) {
            return { product: found, isVariant: false, parentProduct: null }
          }

          // Ищем в variants групповых товаров в избранном
          for (const product of category.products) {
            const productAny = product as any
            if (productAny.type === 'group' && productAny.variants && Array.isArray(productAny.variants)) {
              const variantFound = productAny.variants.find((v: any) =>
                String(v.id) === String(productId),
              )
              if (variantFound) {
                return {
                  product: variantFound,
                  isVariant: true,
                  parentProduct: product,
                }
              }
            }
          }
        }
      }
    }

    return { product: null, isVariant: false, parentProduct: null }
  }

  /**
   * Копировать текст в буфер обмена (универсальный метод)
   */
  async function copyToClipboard(text: string): Promise<void> {
    if (process.client) {
      try {
        await navigator.clipboard.writeText(text)
      } catch {
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.append(textArea)
        textArea.focus()
        textArea.select()

        try {
          document.execCommand('copy')
        } finally {
          textArea.remove()
        }
      }
    }
  }

  return {
    handleNotificationAction,
    executeAction,
  }
}

